import { Link } from 'react-router'

import { QuantRunDetail } from '@/app/components/QuantRunDetail'
import { QuantRunStatusBadge } from '@/app/components/QuantRunStatusBadge'
import { QuantRunSparkline } from '@/app/components/QuantRunSparkline'
import { useNowTick } from '@/app/hooks/useNowTick'
import { refreshQuantRuns, useQuantRuns } from '@/app/hooks/useQuantRuns'
import { runDurationMs, sortRuns } from '@/app/lib/quant/runOrder'
import { hasMetrics } from '@/app/lib/quant/runMetrics'
import type { Strategy } from '@/app/lib/quant/types'
import { cn } from '@/ui/lib/utils'
import {
  formatDuration,
  formatMaxDrawdownPct,
  formatMetricPct,
  formatReturn,
  formatSharpe,
  formatTimestamp,
  returnTone,
  TONE_CLASS,
} from '@/ui/lib/quantFormat'
import { QuantUnavailable } from '@/ui/QuantUnavailable'

const BASE_GRID = 'grid grid-cols-[96px_minmax(130px,1fr)_76px_56px_136px_84px] items-center gap-x-4'
// Spec 038 P27: three fixed 72px metric columns, only at 1,100px and wider; narrower, the base six columns stay.
const METRIC_GRID = `${BASE_GRID} min-[1100px]:grid-cols-[96px_minmax(130px,1fr)_76px_56px_136px_84px_72px_72px_72px]`
const METRIC_CELL = 'hidden min-[1100px]:block truncate text-right text-[11.5px] text-paper-90'

/** The Runs tab (spec 036 D-4, handoff 3.4): the runs list, or one run's detail. */
export function QuantRunsPane({ strategy, runId }: { strategy: Strategy; runId?: string }) {
  if (runId !== undefined) return <QuantRunDetail key={runId} strategy={strategy} runId={runId} />
  return <RunsList slug={strategy.slug} />
}

function RunsList({ slug }: { slug: string }) {
  const state = useQuantRuns()
  const runs = state.status === 'loaded' ? sortRuns(state.runs.filter((r) => r.strategy_slug === slug)) : []
  const now = useNowTick(runs.some((r) => r.status === 'running'))
  // The columns exist only when the server sent metrics for at least one listed run; until then the list is unchanged.
  const showMetrics = runs.some(hasMetrics)
  const GRID = showMetrics ? METRIC_GRID : BASE_GRID

  return (
    <div data-pane="runs" className="absolute inset-0 overflow-y-auto">
      <div className="px-7 pt-2 pb-10">
        <div
          className={cn(
            GRID,
            'py-2.5 pb-2 font-mono text-[9px] tracking-[0.16em] text-paper-40 uppercase',
          )}
        >
          <span>Status</span>
          <span>Started</span>
          <span>Duration</span>
          <span className="text-right">Trades</span>
          <span>Equity</span>
          <span className="text-right">Return</span>
          {showMetrics && (
            <>
              <span data-run-metric-head="max_drawdown_pct" className="hidden truncate text-right min-[1100px]:block">Max DD</span>
              <span data-run-metric-head="win_rate_pct" className="hidden truncate text-right min-[1100px]:block">Win rate</span>
              <span data-run-metric-head="sharpe" className="hidden truncate text-right min-[1100px]:block">Sharpe</span>
            </>
          )}
        </div>
        {(state.status === 'error' || (state.status === 'loaded' && state.error !== undefined)) && (
          <div className="border-t border-paper-14 py-3">
            <QuantUnavailable
              message={state.status === 'error' ? state.message : (state.error ?? '')}
              onRetry={state.status === 'error' ? state.retry : () => void refreshQuantRuns()}
            />
          </div>
        )}
        {state.status === 'loaded' && runs.length === 0 && (
          <div className="border-t border-paper-14 py-[22px] font-mono text-[12px] text-paper-40">
            No runs for this strategy yet. Start one from Config.
          </div>
        )}
        {runs.map((run) => {
          const tone = run.status === 'completed' ? returnTone(run.return_pct) : 'none'
          const duration = runDurationMs(run, now)
          const m = run.metrics ?? null
          const dd = formatMaxDrawdownPct(m?.max_drawdown_pct)
          const win = formatMetricPct(m?.win_rate_pct)
          const sharpe = formatSharpe(m?.sharpe, m?.sharpe_basis)
          return (
            <Link
              key={run.id}
              to={`/strategies/${slug}/runs/${run.id}`}
              data-run-row={run.id}
              data-run-id={run.id}
              data-run-status={run.status}
              className={cn(
                GRID,
                'h-[49px] border-t border-paper-14 font-mono text-paper no-underline hover:bg-paper-14',
              )}
            >
              <span>
                <QuantRunStatusBadge status={run.status} cancelRequestedAt={run.cancel_requested_at} />
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[12px] text-paper-90">{formatTimestamp(run.started_at ?? run.created_at)}</span>
                <span className="truncate text-[10px] text-paper-40" title={run.id}>
                  {run.id}
                </span>
              </span>
              <span className="text-[11.5px] text-paper-50">{formatDuration(duration)}</span>
              <span className="text-right text-[11.5px] text-paper-90">{run.trades ?? '—'}</span>
              <QuantRunSparkline run={run} width={136} height={28} />
              <span className={cn('text-right text-[13px]', TONE_CLASS[tone])} data-run-return>
                {run.status === 'completed' ? formatReturn(run.return_pct) : '—'}
              </span>
              {showMetrics && (
                <>
                  <span data-run-metric="max_drawdown_pct" className={METRIC_CELL} title={dd.title}>{dd.text}</span>
                  <span data-run-metric="win_rate_pct" className={METRIC_CELL} title={win.title}>{win.text}</span>
                  <span data-run-metric="sharpe" className={METRIC_CELL} title={m === null ? undefined : sharpe.title}>{sharpe.text}</span>
                </>
              )}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
